/**
 * Sibling Products (Color) switch without full page reload.
 * Uses section_id approach for OS2 themes.
 * Prefetch only on hover-capable devices (desktop).
 *
 * PATCH SET (ALL IN ONE):
 * 1) Early intercept on mobile (pointerdown + touchstart) in CAPTURE.
 * 2) Click handler moved to CAPTURE to win listener races.
 * 3) Robustness around .product__siblings roots:
 *    - Update active state across ALL roots (in case of duplicates)
 *    - Lightweight diagnostics if 0 or >1 roots exist
 */
(function () {
  'use strict';

  // Cache TTL in ms (2 minutes)
  const CACHE_TTL = 120000;

  const cache = new Map();
  let activeSwitchId = 0;
  let activeController = null;

  const CAN_HOVER = window.matchMedia("(hover: hover)").matches;

  function isModifiedClick(evt) {
    return evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button !== 0;
  }

  function normalizeUrl(href) {
    try { return new URL(href, window.location.origin); }
    catch { return null; }
  }

  function isSameUrl(a, b) {
    return a.pathname === b.pathname && a.search === b.search;
  }

  function getCache(key) {
    const hit = cache.get(key);
    if (!hit) return null;
    if (!CACHE_TTL) return hit;
    if (Date.now() - hit.ts <= CACHE_TTL) return hit;
    cache.delete(key);
    return null;
  }

  function getCurrentMainWrapper() {
    const anchor = document.querySelector('product-info, product-form, variant-selects');
    return anchor?.closest("[id^='shopify-section-']") || null;
  }

  function getCurrentSectionInstanceId() {
    const wrapper = getCurrentMainWrapper();
    if (!wrapper) return null;
    return wrapper.id.replace(/^shopify-section-/, '');
  }

  async function fetchSectionHtml(targetUrl, sectionInstanceId, { signal } = {}) {
    const url = new URL(targetUrl.toString());
    url.searchParams.set('section_id', sectionInstanceId);

    const key = url.toString();
    const cached = getCache(key);
    if (cached) return cached;

    const res = await fetch(key, {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!res.ok) throw new Error(`Section fetch failed (${res.status})`);

    const html = await res.text();
    const payload = { html, ts: Date.now() };
    cache.set(key, payload);
    return payload;
  }

  function replaceMainSection(htmlString, sectionInstanceId) {
    const currentWrapper = getCurrentMainWrapper();
    if (!currentWrapper) return false;

    const parsed = new DOMParser().parseFromString(htmlString, 'text/html');
    const incomingWrapperId = `shopify-section-${sectionInstanceId}`;
    const incomingWrapper = parsed.getElementById(incomingWrapperId);

    if (incomingWrapper) {
      currentWrapper.replaceWith(incomingWrapper);
      return true;
    }

    const fragment = parsed.body?.innerHTML?.trim();
    if (fragment) {
      currentWrapper.innerHTML = fragment;
      return true;
    }

    return false;
  }

  // ---- PATCH 3: work across ALL sibling roots + diagnostics ----
  function getSiblingRoots() {
    return Array.from(document.querySelectorAll('.product__siblings'));
  }

  function diagnoseSiblingRoots() {
    const roots = getSiblingRoots();
    if (roots.length === 0) {
      // Not fatal (theme may not render siblings on some products), but useful during debugging
      // eslint-disable-next-line no-console
      console.warn('[siblings] No .product__siblings root found on page.');
    } else if (roots.length > 1) {
      // Multiple roots can happen after theme edits/sections; not fatal but can create inconsistent UI
      // eslint-disable-next-line no-console
      console.warn('[siblings] Multiple .product__siblings roots found:', roots.length);
    }
    return roots;
  }

  function setActiveSiblingSwatch(targetUrl) {
    const roots = getSiblingRoots();
    if (!roots.length) return;

    roots.forEach((root) => {
      root.querySelectorAll('a[href]').forEach((a) => {
        const u = normalizeUrl(a.getAttribute('href'));
        if (!u) return;

        // Active by pathname (color sibling usually changes handle/path)
        const active = u.pathname === targetUrl.pathname;
        a.classList.toggle('active', active);

        if (active) a.setAttribute('aria-current', 'page');
        else a.removeAttribute('aria-current');
      });
    });
  }

  async function switchSibling(targetUrl) {
    const sectionId = getCurrentSectionInstanceId();
    if (!sectionId) {
      window.location.href = targetUrl.toString();
      return;
    }

    if (activeController) {
      try { activeController.abort(); } catch (_) {}
    }

    const switchId = ++activeSwitchId;
    const controller = new AbortController();
    activeController = controller;

    try {
      const { html } = await fetchSectionHtml(targetUrl, sectionId, { signal: controller.signal });

      if (switchId !== activeSwitchId) return;

      // TATES PATCH: Preserve Avada button before replace
      const avadaButton = document.querySelector('#Avada-SC-button');
      const avadaParent = avadaButton?.parentElement;
      let avadaSibling = null;
      
      if (avadaButton && avadaParent) {
        // Remember position (before which sibling to insert)
        avadaSibling = avadaButton.nextSibling;
        // Remove from DOM but keep reference
        avadaButton.remove();
        console.log('[TATES] Preserved Avada button');
      }

      const ok = replaceMainSection(html, sectionId);
      if (!ok) {
        window.location.href = targetUrl.toString();
        return;
      }

      // TATES PATCH: Restore Avada button after replace
      if (avadaButton) {
        setTimeout(() => {
          // Find the same parent in new DOM
          const newParent = document.querySelector('variant-selects fieldset.product-form__input > div.flex');
          
          if (newParent) {
            // Insert at same position
            if (avadaSibling && newParent.contains(avadaSibling)) {
              newParent.insertBefore(avadaButton, avadaSibling);
            } else {
              // Append at end if position not found
              newParent.appendChild(avadaButton);
            }
            console.log('[TATES] ✅ Restored Avada button');
          } else {
            console.warn('[TATES] Could not find target to restore Avada button');
          }
        }, 50);
      }

      window.history.replaceState({}, '', targetUrl.pathname + targetUrl.search);

      // TATES PATCH: Re-execute inline scripts in new section
      const newSection = getCurrentMainWrapper();
      if (newSection) {
        const inlineScripts = newSection.querySelectorAll('script:not([src])');
        inlineScripts.forEach((oldScript) => {
          const newScript = document.createElement('script');
          newScript.textContent = oldScript.textContent;
          
          // Copy attributes
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          
          // Replace old with new to trigger execution
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
        
        console.log('[TATES] ✅ Re-executed', inlineScripts.length, 'inline scripts');
      }

      // PATCH 3: update all roots after DOM swap + quick diagnosis
      diagnoseSiblingRoots();
      setActiveSiblingSwatch(targetUrl);

      document.dispatchEvent(
        new CustomEvent('siblings:changed', { detail: { url: targetUrl.toString() } })
      );

      // TATES PATCH: Additional polling for async widgets
      setTimeout(() => {
        console.log('[TATES] Checking for async widget initialization...');

        // Poll for Avada
        let avadaAttempts = 0;
        const avadaPoll = setInterval(() => {
          avadaAttempts++;
          
          if (document.querySelector('#Avada-SC-button') || window.AvadaSizeChart) {
            clearInterval(avadaPoll);
            console.log('[TATES] ✅ Avada initialized');
          } else if (avadaAttempts >= 30) {
            clearInterval(avadaPoll);
            console.log('[TATES] ⚠️ Avada not found (may not be enabled for this product)');
          }
        }, 100);

        // Poll for AI Size Guide
        let aiAttempts = 0;
        const aiPoll = setInterval(() => {
          aiAttempts++;
          
          const aiButton = document.querySelector('#ai-size-guide-root button');
          if (aiButton && aiButton.onclick) {
            clearInterval(aiPoll);
            console.log('[TATES] ✅ AI Size Guide initialized');
          } else if (aiAttempts >= 30) {
            clearInterval(aiPoll);
            console.log('[TATES] ⚠️ AI Size Guide button not interactive');
          }
        }, 100);
      }, 200);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      window.location.href = targetUrl.toString();
    } finally {
      if (switchId === activeSwitchId) activeController = null;
    }
  }

  function prefetchSibling(targetUrl) {
    const sectionId = getCurrentSectionInstanceId();
    if (!sectionId) return;
    fetchSectionHtml(targetUrl, sectionId).catch(() => {});
  }

  function isInterceptableSiblingLink(a) {
    const href = a?.getAttribute?.('href');
    if (!href) return false;
    const u = normalizeUrl(href);
    return !!u && u.origin === window.location.origin;
  }

  function shouldHandleLink(evt, a) {
    if (!a) return null;

    // On pointerdown/touchstart there is no reliable evt.button -> only modifiers there.
    // On click we additionally check button via isModifiedClick().
    if (evt.type === 'click') {
      if (isModifiedClick(evt)) return null;
    } else {
      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return null;
    }

    if (!isInterceptableSiblingLink(a)) return null;

    const targetUrl = normalizeUrl(a.getAttribute('href'));
    if (!targetUrl) return null;

    const currentUrl = new URL(window.location.href);

    // Same full URL? do nothing.
    if (isSameUrl(targetUrl, currentUrl)) return null;

    // Extra guard: if only hash differs, also ignore (rare)
    if (targetUrl.pathname === currentUrl.pathname && targetUrl.search === currentUrl.search) return null;

    return targetUrl;
  }

  // ---- PATCH 1: Early intercept (mobile) ----
  function handleEarly(evt) {
    const a = evt.target?.closest?.('.product__siblings a[href]');
    const targetUrl = shouldHandleLink(evt, a);
    if (!targetUrl) return;

    evt.preventDefault();
    evt.stopPropagation();
    if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();

    switchSibling(targetUrl);
  }

  // pointer events first (best)
  document.addEventListener('pointerdown', handleEarly, true);

  // iOS fallback if pointer events are not firing for some reason
  document.addEventListener('touchstart', handleEarly, { capture: true, passive: false });

  // ---- PATCH 2: Click in CAPTURE (fallback + wins race) ----
  document.addEventListener('click', (evt) => {
    const a = evt.target?.closest?.('.product__siblings a[href]');
    const targetUrl = shouldHandleLink(evt, a);
    if (!targetUrl) return;

    evt.preventDefault();
    evt.stopPropagation();
    if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();

    switchSibling(targetUrl);
  }, true);

  // PREFETCH ONLY ON HOVER-CAPABLE DEVICES (DESKTOP)
  if (CAN_HOVER) {
    document.addEventListener(
      'mouseover',
      (evt) => {
        const a = evt.target.closest('.product__siblings a[href]');
        if (!a) return;
        if (!isInterceptableSiblingLink(a)) return;

        const targetUrl = normalizeUrl(a.getAttribute('href'));
        if (targetUrl) prefetchSibling(targetUrl);
      },
      { passive: true }
    );
  }

  // PATCH 3: Initial diagnosis on first load (helps spot duplicates early)
  diagnoseSiblingRoots();
})();